'use client';

import type { AiReviewSettingsTab } from '@/lib/ai-review-settings/cards';
import { platform } from '@todesktop/client-core';
import { ArrowRight, ExternalLink, Keyboard, LogOut, Share } from 'lucide-react';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { memo } from 'react';
import { toast } from 'sonner';

import { COMMAND_GROUPS } from '../types';
import { useRegisterCommands } from '../use-commands';
import {
  buildAiReviewSettingsCommands,
  getAiReviewSettingsDisplayLabel,
} from './ai-review-settings-command-factory';
import { buildSettingsCommands } from './settings-command-factory';

import { useHasCodeAccess } from '@/data/code-sessions/use-has-code-access';
import { useReferralCode } from '@/data/referral/use-referral-code';
import { useClipboard } from '@/hooks/use-clipboard';
import {
  AI_REVIEW_TABS,
  SETTINGS_ACTION_GET_MORE_USAGE,
  SETTINGS_ACTION_PARAM,
  SETTINGS_TABS,
} from '@/lib/constants';
import { shouldEnableKeyboardShortcutsDrawer } from '@/lib/keyboard-shortcuts/registry';
import { useKeyboardShortcutsDrawerStore } from '@/lib/stores/keyboard-shortcuts-drawer-store';
import { useAuth } from '@/providers/auth-provider';
import { buildSettingsPath } from '@/utils/app-paths';

const CUSTOM_AGENTS_COMMAND_LABEL = 'AI review - Custom agents';
const SENIOR_REVIEWERS_COMMAND_LABEL = 'AI review - Senior reviewers';

/** The AI review page opens on General review when the URL names no tab. */
function isAiReviewTabOpen(
  path: string,
  params: ReadonlyURLSearchParams | undefined,
  tab: AiReviewSettingsTab,
) {
  return path.startsWith('/ai-review') && (params?.get('tab') ?? AI_REVIEW_TABS.SETUP) === tab;
}

/**
 * Global commands provider for core app functionality
 * These commands are available throughout the application
 */
export const GlobalCommands = memo(() => {
  const router = useRouter();
  const { user, signOut } = useAuth();
  const { data: referralData } = useReferralCode();
  const hasCodeAccess = useHasCodeAccess();
  const { copy } = useClipboard({ successMessage: 'Referral link copied' });
  const openKeyboardShortcuts = useKeyboardShortcutsDrawerStore((state) => state.openDrawer);

  // Import the handlers from user-nav.tsx
  const handleContactUs = () => {
    window.location.href = 'mailto:contact@cubic.dev?subject=Contact%20from%20cubic';
  };

  const handleDownloadDesktop = () => {
    router.push('/download');
  };

  // Now, define commands inside the hook call
  useRegisterCommands(
    () => [
      // General and navigation commands
      {
        id: 'show-keyboard-shortcuts',
        label: 'Show keyboard shortcuts',
        icon: <Keyboard />,
        action: openKeyboardShortcuts,
        shortcutId: 'global-show-shortcuts',
        keywords: ['keyboard', 'shortcuts', 'hotkeys', 'help', 'reference'],
        showOn: shouldEnableKeyboardShortcutsDrawer,
        group: COMMAND_GROUPS.GENERAL.name,
        priority: 5000,
      },
      {
        id: 'home',
        label: 'Go to Pull requests',
        icon: <ArrowRight />,
        action: () => router.push('/inbox'),
        shortcutId: 'global-go-inbox',
        keywords: ['inbox', 'pull requests', 'prs', 'dashboard', 'main', 'landing', 'home'],
        description: 'Open your pull requests.',
        examples: ['open inbox', 'go home', 'show dashboard'],
        safeExecution: 'navigation',
        // Hide when already on the inbox
        showOn: (path) => path !== '/inbox',
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 200,
      },
      {
        id: 'dashboard',
        label: 'Go to Dashboard',
        icon: <ArrowRight />,
        action: () => router.push('/dashboard'),
        keywords: ['dashboard', 'overview', 'activity', 'quick start', 'stats'],
        description: 'Open the dashboard with the last 30 days of review activity.',
        examples: ['open dashboard', 'show overview', 'review activity'],
        safeExecution: 'navigation',
        showOn: (path) => path !== '/dashboard',
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 3850,
      },
      {
        id: 'settings',
        label: 'Go to Settings',
        icon: <ArrowRight />,
        action: () => router.push('/settings'),
        shortcutId: 'global-go-settings',
        keywords: ['settings', 'preferences', 'config', 'options'],
        description: 'Open the main settings page.',
        examples: ['open settings', 'change preferences', 'configure cubic'],
        safeExecution: 'navigation',
        // Hide when already on settings
        showOn: (path) => !path.startsWith('/settings'),
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 3200,
      },
      {
        id: 'get-more-usage',
        label: 'Get more usage',
        icon: <ArrowRight />,
        action: () =>
          router.push(
            buildSettingsPath({
              tab: SETTINGS_TABS.USAGE,
              params: { [SETTINGS_ACTION_PARAM]: SETTINGS_ACTION_GET_MORE_USAGE },
            }),
          ),
        keywords: [
          'flex',
          'flex capacity',
          'top up',
          'unlock',
          'blocked',
          'paused',
          'resume reviews',
          'spend limit',
          'raise limit',
          'more usage',
          'quota',
        ],
        description: 'Open the Get more usage dialog to set or raise your flex spend limit.',
        examples: ['top up flex capacity', 'unlock reviews', 'raise spend limit'],
        safeExecution: 'navigation',
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 3110,
      },
      {
        id: 'settings-ai-review',
        label: 'Go to AI review settings',
        icon: <ArrowRight />,
        action: () => router.push(`/ai-review?tab=${AI_REVIEW_TABS.SETUP}`),
        keywords: ['ai', 'review', 'code', 'analysis', 'repository'],
        description: 'Open repository AI review settings.',
        examples: ['configure ai review', 'review settings', 'repository review config'],
        safeExecution: 'navigation',
        showOn: (path, params) => !isAiReviewTabOpen(path, params, AI_REVIEW_TABS.SETUP),
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 3050,
      },
      ...buildAiReviewSettingsCommands((href) => router.push(href), {
        priorityBase: 2450,
      }),
      ...buildSettingsCommands((href) => router.push(href)),
      {
        id: 'analytics',
        label: 'Go to Analytics',
        icon: <ArrowRight />,
        action: () => router.push('/analytics'),
        keywords: ['analytics', 'insights', 'metrics', 'reports'],
        description: 'Open analytics, insights, metrics, and reports.',
        examples: ['show analytics', 'open metrics', 'view reports'],
        safeExecution: 'navigation',
        showOn: (path) => !path.startsWith('/analytics'),
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 2750,
      },
      {
        id: 'wiki',
        label: 'Go to Wiki',
        icon: <ArrowRight />,
        action: () => router.push('/wiki'),
        keywords: ['wiki', 'docs', 'documentation', 'ai docs', 'repository docs'],
        description: "Open the wiki of AI-written docs for your team's repositories.",
        examples: ['open wiki', 'repository docs', 'show documentation'],
        safeExecution: 'navigation',
        showOn: (path) => path !== '/wiki',
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 2740,
      },
      {
        id: 'codebase-scan',
        label: 'Go to Codebase scan',
        icon: <ArrowRight />,
        action: () => router.push('/codebase-scan'),
        keywords: [
          'codebase scan',
          'codebase scans',
          'scan',
          'scans',
          'deep scan',
          'security',
          'findings',
        ],
        description: 'Open the codebase scan dashboard.',
        examples: ['open scans', 'codebase scan findings', 'security scan'],
        safeExecution: 'navigation',
        showOn: (path) => path !== '/codebase-scan',
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 2700,
      },
      ...(hasCodeAccess
        ? [
            {
              id: 'code',
              label: 'Go to Code',
              icon: <ArrowRight />,
              action: () => router.push('/code'),
              keywords: ['code', 'coding agent', 'sessions', 'new session', 'agent'],
              description: 'Open Code to start or continue a coding agent session.',
              examples: ['open code', 'start a coding session', 'coding agent'],
              safeExecution: 'navigation' as const,
              showOn: (path: string) => path !== '/code',
              group: COMMAND_GROUPS.NAVIGATION.name,
              priority: 2690,
            },
          ]
        : []),
      {
        id: 'custom-rules',
        label: CUSTOM_AGENTS_COMMAND_LABEL,
        displayLabel: getAiReviewSettingsDisplayLabel(CUSTOM_AGENTS_COMMAND_LABEL),
        icon: <ArrowRight />,
        action: () => router.push(`/ai-review?tab=${AI_REVIEW_TABS.CUSTOM_RULES}`),
        shortcutId: 'global-go-custom-agents',
        keywords: ['ai review', 'custom agents', 'agents', 'rules', 'policy', 'configuration'],
        description: 'Open AI review custom agents and custom rule settings.',
        examples: ['custom agents', 'configure rules', 'review policies'],
        safeExecution: 'navigation',
        showOn: (path, params) => !isAiReviewTabOpen(path, params, AI_REVIEW_TABS.CUSTOM_RULES),
        group: COMMAND_GROUPS.AI_REVIEW_SETTINGS.name,
        priority: 2300,
      },
      {
        id: 'senior-reviewers',
        label: SENIOR_REVIEWERS_COMMAND_LABEL,
        displayLabel: getAiReviewSettingsDisplayLabel(SENIOR_REVIEWERS_COMMAND_LABEL),
        icon: <ArrowRight />,
        action: () => router.push(`/ai-review?tab=${AI_REVIEW_TABS.SENIOR_REVIEWERS}`),
        keywords: [
          'ai review',
          'senior reviewers',
          'reviewers',
          'learning',
          'learnings',
          'memory',
          'team patterns',
          'review style',
        ],
        description: 'Open AI review senior reviewers and learned review patterns.',
        examples: ['senior reviewers', 'team review style', 'reviewer learnings'],
        safeExecution: 'navigation',
        showOn: (path, params) => !isAiReviewTabOpen(path, params, AI_REVIEW_TABS.SENIOR_REVIEWERS),
        group: COMMAND_GROUPS.AI_REVIEW_SETTINGS.name,
        priority: 2290,
      },
      {
        id: 'ai-review',
        label: 'Go to AI review',
        icon: <ArrowRight />,
        action: () => router.push('/ai-review'),
        shortcutId: 'global-go-ai-review',
        keywords: ['review', 'ai', 'code', 'review'],
        description: 'Open the AI review configuration page.',
        examples: ['open ai review', 'review configuration', 'ai review page'],
        safeExecution: 'navigation',
        // Hide when already viewing the AI review page
        showOn: (path) => path !== '/ai-review',
        group: COMMAND_GROUPS.NAVIGATION.name,
        priority: 3800,
      },
      {
        id: 'logout',
        label: 'Log out',
        icon: <LogOut />,
        action: async () => {
          try {
            await signOut();
            router.push('/sign-in');
          } catch (error) {
            console.error('Error during sign out:', error);
          }
        },
        keywords: ['sign out', 'exit', 'quit'],
        group: COMMAND_GROUPS.GENERAL.name,
        priority: 100,
        searchOnly: true,
      },

      // User-related commands from user-nav.tsx
      {
        id: 'copy-referral-link',
        label: 'Copy referral link',
        icon: <Share />,
        action: async () => {
          if (referralData?.inviteUrl) {
            await copy(referralData.inviteUrl);
          } else if (user) {
            toast.error('Referral link is not available yet. Please try again.');
          } else {
            toast.error('You need to be logged in to copy your referral link');
          }
        },
        keywords: [
          'referral',
          'refer',
          'invite',
          'team',
          'share',
          'colleagues',
          'coworkers',
          'members',
          'link',
          'copy',
        ],
        group: COMMAND_GROUPS.GENERAL.name,
        searchOnly: true,
      },

      // Get desktop app command - only shown when not in desktop app
      {
        id: 'get-desktop-app',
        label: 'Get cubic for desktop',
        icon: <ExternalLink />,
        action: handleDownloadDesktop,
        keywords: ['desktop', 'app', 'download', 'client', 'local'],
        showOn: () => !platform.todesktop.isDesktopApp(),
        group: COMMAND_GROUPS.GENERAL.name,
        searchOnly: true,
      },

      // Contact us command
      {
        id: 'contact-us',
        label: 'Contact us',
        icon: <ExternalLink />,
        action: handleContactUs,
        keywords: ['email', 'support', 'help', 'contact'],
        group: COMMAND_GROUPS.GENERAL.name,
        searchOnly: true,
      },
    ],
    [copy, hasCodeAccess, openKeyboardShortcuts, referralData?.inviteUrl, router, signOut, user],
  );

  // This component doesn't render anything visible
  return null;
});
